import {mesosConfig} from "../../config/mesos-config";
import {ReplyParsed} from "./reply-parsed";
import {uuid} from "../uuid";
import {http} from "../http";
import {Reply} from "./reply";
import {withSanitization} from "./with-sanitization";
import {withReferrerPolicy} from "./with-referrer-policy";
import {parseReply} from "./parse-reply";
import {BotId} from "../../global/models/models";
import {TECHNICAL_ERROR, MAIL_TECHNICAL_ERROR} from "../../global/variables";

export interface MessageParams {
    message: string;
    sessionId: string;
    conversationId: string;
    botId?: BotId;
    clientID?: string;
    status?: number;
    img?: string;
}

export type PostMessageParams = MessageParams;

export class Backend {
    private readonly clientID: string;
    private readonly httpClient = http;
    private readonly baseUrl = mesosConfig.chatbotApi;
    readonly uuidFn = uuid;

    withReferrerPolicy = withReferrerPolicy;
    withSanitization = withSanitization;
    parseReply = parseReply

    async message(messageParams: MessageParams): Promise<ReplyParsed> {
        if (messageParams.clientID === undefined) {
            messageParams.clientID ||= this.clientID;
        }
        const reply = await this.postMessage({
            ...messageParams
        });
        return this.withReferrerPolicy(
            this.withSanitization(await this.parseReply(reply))
        );
    }

    async postMessage(
        postMessageParams: PostMessageParams
    ): Promise<Reply> {
        const headers = {
            "X-API-Key": "chatbot",
            "Content-Type": "application/json",
            "Session-ID": postMessageParams.sessionId,
            "Conversation-ID": postMessageParams.conversationId,
            "Correlation-ID": this.uuidFn(),
            "Bot-ID": postMessageParams.botId,
        };
        const url = this.baseUrl + "/webhooks/rest/webhook";
        const method = "POST";
        const request = {
            url,
            method,
            headers,
            body: JSON.stringify({
                message: postMessageParams.message,
                sender: postMessageParams.conversationId,
            }),
            timeout: 100000,
        };

        try {
            const botReply = await this.httpClient<Reply>(request);

            return this.handleError(botReply, postMessageParams);
        } catch (e) {
            const status = e?.status ?? 500;

            return this.handleError(
                { status },
                postMessageParams
            );
        }
    }

    private handleError(botReply: any, postMessageParams: MessageParams) {
        const status = botReply.status || 500;
        if (!(status >= 200 && status < 300)) {
            return {
                data: [{
                    recipient_id: postMessageParams.conversationId,
                    custom: {
                        botId: postMessageParams.botId,
                        messages: [
                            {
                                text: ((status === 503) ? (`Error Code: ${status}<br/>` + MAIL_TECHNICAL_ERROR) : (`Error Code: ${status}<br/>` + TECHNICAL_ERROR)),
                                type: "defaultText",
                            },
                        ],
                        answerBy: {type: "textInput"},
                    },
                }],
            };
        } else {
            botReply.botId = postMessageParams.botId;
            botReply.clientID = postMessageParams.clientID;
            return botReply;
        }
    }
}
